import React, { useEffect, useRef } from 'react';
import { TherapistAboutProps } from '../../types';

/**
 * Componente Education renovado con diseño innovador y animaciones
 */
const TherapistEducation: React.FC<TherapistAboutProps> = ({ therapist }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Configurar el Intersection Observer para animaciones scroll-triggered
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    // Observar todos los elementos animables
    const animatableElements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    animatableElements?.forEach(el => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} id="education" className="therapist-education-modern">
      <div className="container">
        {/* Header moderno */}
        <div className="modern-section-header animate-on-scroll">
          <div className="header-decoration"></div>
          <h2 className="modern-section-title">
            <span className="title-highlight">Formación</span> & Trayectoria
          </h2>
          <p className="modern-section-subtitle">
            Excelencia académica y experiencia profesional demostrada
          </p>
        </div>

        {/* Grid Principal - Diseño Visual Atractivo */}
        <div className="education-visual-grid">
          
          {/* Card Principal - Educación */}
          <div className="education-hero-card animate-on-scroll">
            <div className="card-visual-header">
              <div className="floating-icon education-icon">
                <i className="fas fa-graduation-cap"></i>
                <div className="icon-pulse"></div>
              </div>
              <div className="card-title-section">
                <h3 className="visual-card-title">Educación</h3>
                <p className="visual-card-subtitle">{therapist.personalInfo.education.length} títulos profesionales</p>
                <div className="university-badge">
                  <i className="fas fa-university"></i>
                  <span>Universidad de Santiago de Chile (USACH)</span>
                </div>
              </div>
            </div>
            
            <div className="education-visual-timeline">
              {therapist.personalInfo.education.map((edu, index) => (
                <div key={index} className="education-timeline-item" style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}>
                  <div className="timeline-connector"></div>
                  <div className="timeline-year">{edu.year}</div>
                  <div className="timeline-content">
                    <h4 className="timeline-degree">{edu.degree}</h4>
                    <span className="timeline-institution">{edu.institution}</span>
                    {edu.honors && (
                      <div className="timeline-honors">
                        <i className="fas fa-medal"></i>
                        <span> {edu.honors}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Card Certificaciones */}
          <div className="certifications-hero-card animate-on-scroll">
            <div className="card-visual-header">
              <div className="floating-icon certifications-icon">
                <i className="fas fa-certificate"></i>
                <div className="icon-pulse"></div>
              </div>
              <div className="card-title-section">
                <h3 className="visual-card-title">Certificaciones</h3>
                <p className="visual-card-subtitle">{therapist.personalInfo.certifications.length} certificaciones especializadas</p>
                <div className="status-badge active">
                  <i className="fas fa-check-circle"></i>
                  <span>Actualizadas y vigentes</span>
                </div>
              </div>
            </div>
            
            <div className="certifications-visual-grid">
              {therapist.personalInfo.certifications.map((cert, index) => (
                <div key={index} className="cert-visual-item" style={{ '--delay': `${index * 0.05}s` } as React.CSSProperties}>
                  <div className="cert-visual-icon">
                    <i className="fas fa-award"></i>
                  </div>
                  <div className="cert-visual-content">
                    <h5 className="cert-visual-name">{cert.name}</h5>
                    <p className="cert-visual-institution">{cert.institution}</p>
                    <span className="cert-visual-year">{cert.year}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>


        </div>

        {/* Registro profesional — FUNC-113. Dato público (RNPI): se muestra solo
            si al menos uno de los 2 campos viene poblado (allowlist de columnas
            de `terapeutas`, ver useTerapeutas.ts). */}
        {(therapist.professionalInfo.registroSuperintendencia || therapist.professionalInfo.licenseNumber) && (
          <div className="registry-hero-card animate-on-scroll">
            <div className="card-visual-header">
              <div className="floating-icon registry-icon">
                <i className="fas fa-id-card"></i>
              </div>
              <div className="card-title-section">
                <h3 className="visual-card-title">Registro profesional</h3>
                <p className="visual-card-subtitle">Verificable en el registro oficial de la Superintendencia de Salud</p>
              </div>
            </div>

            <dl className="registry-rows">
              {therapist.professionalInfo.registroSuperintendencia && (
                <div className="registry-row">
                  <dt>N° de registro — Superintendencia de Salud</dt>
                  <dd>{therapist.professionalInfo.registroSuperintendencia}</dd>
                </div>
              )}
              {therapist.professionalInfo.licenseNumber && (
                <div className="registry-row">
                  <dt>N° de registro — Colegio de Psicólogos de Chile</dt>
                  <dd>{therapist.professionalInfo.licenseNumber}</dd>
                </div>
              )}
            </dl>

            <a
              className="registry-rnpi-link"
              href="https://rnpi.superdesalud.gob.cl/"
              target="_blank"
              rel="noreferrer"
            >
              <i className="fas fa-external-link-alt" aria-hidden="true"></i>
              Verificar en el Registro Nacional de Prestadores Individuales (RNPI)
            </a>
          </div>
        )}

      </div>
    </section>
  );
};

export default TherapistEducation;